import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { NotificationPreferenceView, NotificationView } from '@lims/shared-types';
import { notificationsApi } from '../../api/endpoints.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Panel,
  SaveErrorNotice,
  SwitchField,
  useServerForm,
  useToast,
} from '../../components/ui/index.js';
import { formatDate, formatDateTime } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { isolate, notificationTarget } from '../quality/operations-ui.js';
import { ViewTabPanel, ViewTabs } from '../work/work-ui.js';

/**
 * The sentence a notification reads as, in the reader's language.
 *
 * Shared by this page and the bell's panel in the top bar, so one notification
 * is worded identically wherever it is shown. Display values only; a date is
 * shown in the reader's calendar format.
 */
export function useNotificationText(): (item: NotificationView) => string {
  const { t } = useTranslation();
  const locale = currentLocale();
  return (item) => {
    const params: Record<string, string> = {};
    for (const [key, value] of Object.entries(item.params)) {
      if (value === null || value === undefined) continue;
      params[key] = isolate(key === 'dueOn' ? (formatDate(String(value), { locale }) ?? String(value)) : String(value));
    }
    if (item.kind === 'result_returned') {
      params.stage = t(`notifications.stage.${item.params.stage === 'approval' ? 'approval' : 'review'}`);
    }
    return t(`notificationText.${item.kind}`, params);
  };
}

/**
 * The caller's own notification centre (Phase 12, D169–D172).
 *
 * Every notification leads DIRECTLY to its work item: opening one marks it
 * read and goes to the page that shows the item. The page itself re-checks the
 * caller's permission, as every page does.
 */
export function NotificationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const locale = currentLocale();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page, setPage] = useState(1);

  const list = useQuery({
    queryKey: ['notifications', 'list', { unreadOnly, page }],
    queryFn: () => notificationsApi.list({ page, ...(unreadOnly ? { unreadOnly: 'true' } : {}) }),
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };
  const readAll = useMutation({ mutationFn: () => notificationsApi.markAllRead(), onSuccess: refresh });

  const open = async (item: NotificationView) => {
    if (!item.readAt) {
      await notificationsApi.markRead(item.id);
      await refresh();
    }
    const target = notificationTarget(item);
    if (target) navigate(target);
  };

  const text = useNotificationText();

  const rows = list.data?.data ?? [];
  const totalPages = list.data?.meta.totalPages ?? 0;
  const tab = unreadOnly ? 'unread' : 'all';

  return (
    <>
      <PageHeader
        title={t('notifications.title')}
        subtitle={t('notifications.subtitle')}
        breadcrumbs={[{ label: t('nav.notifications') }]}
        actions={
          <>
            <ActionButton priority="tertiary" onPress={() => navigate('/notifications/settings')}>
              {t('notifications.settingsLink')}
            </ActionButton>
            <ActionButton priority="secondary" isLoading={readAll.isPending} onPress={() => readAll.mutate()}>
              {t('notifications.markAllRead')}
            </ActionButton>
          </>
        }
      />
      {/* "All" and "Unread" are the old "Only unread" checkbox, as tabs: the
          same state and the same request. */}
      <ViewTabs
        label={t('notifications.title')}
        idBase="notifications"
        value={tab}
        tabs={[
          { value: 'all', label: t('notifications.all') },
          { value: 'unread', label: t('notifications.unread') },
        ]}
        onChange={(value) => {
          setUnreadOnly(value === 'unread');
          setPage(1);
        }}
      />
      <div className="mt-stack">
        <ViewTabPanel idBase="notifications" value={tab}>
          {list.isLoading ? (
            <LoadingState label={t('common.loading')} />
          ) : list.error ? (
            <ErrorState error={list.error} onRetry={() => void list.refetch()} />
          ) : rows.length === 0 ? (
            <EmptyState title={unreadOnly ? t('notifications.allCaughtUp') : t('notifications.none')} />
          ) : (
            <section className="rounded-large border border-default-200 bg-white p-1">
              <ul className="flex flex-col">
                {rows.map((item) => (
                  <li key={item.id}>
                    {/* The row is the way in: opening marks it read and goes to
                        the work it is about, exactly as the bell does. */}
                    <button
                      type="button"
                      onClick={() => void open(item)}
                      className={`flex w-full items-start gap-3 rounded-medium px-3 py-2.5 text-start outline-none transition-colors hover:bg-default-100 focus-visible:ring-2 focus-visible:ring-primary-500 ${
                        item.readAt ? '' : 'bg-primary-50/70'
                      }`}
                    >
                      <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-full border border-default-200 bg-default-50 text-default-700">
                        <BellGlyph />
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className={`text-body ${item.readAt ? 'text-default-800' : 'font-semibold text-default-900'}`}>
                          {text(item)}
                        </span>
                        <span className={`text-caption ${item.readAt ? 'text-default-600' : 'font-semibold text-primary-700'}`}>
                          {t(`notificationKind.${item.kind}`)} · {formatDateTime(item.createdAt, { locale })}
                        </span>
                      </span>
                      {item.readAt ? (
                        <span className="sr-only">{t('notifications.read')}</span>
                      ) : (
                        <span className="mt-2 size-2 shrink-0 rounded-full bg-primary-600">
                          <span className="sr-only">{t('notifications.unread')}</span>
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </ViewTabPanel>
      </div>
      {totalPages > 1 ? (
        <div className="mt-stack">
          <ActionBar>
            <ActionButton priority="tertiary" isDisabled={page <= 1} onPress={() => setPage(page - 1)}>
              {t('common.previous')}
            </ActionButton>
            <span className="text-body text-default-700">{t('notifications.pageOf', { page, total: totalPages })}</span>
            <ActionButton priority="tertiary" isDisabled={page >= totalPages} onPress={() => setPage(page + 1)}>
              {t('common.next')}
            </ActionButton>
          </ActionBar>
        </div>
      ) : null}
    </>
  );
}

/** The bell drawn in each row, matching the top bar's panel. */
function BellGlyph() {
  return (
    <svg
      width={15}
      height={15}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M6 16V11a6 6 0 0 1 12 0v5l2 2H4z" />
      <path d="M10 21h4" />
    </svg>
  );
}

/**
 * The caller's own preferences, one per kind (D172). Email cannot be switched
 * on while the installation has no email provider (D162); the page says so
 * rather than offering a switch that would do nothing.
 */
export function NotificationSettingsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { show } = useToast();
  const prefs = useQuery({ queryKey: ['notifications', 'preferences'], queryFn: () => notificationsApi.preferences() });
  // Authoritative while edited; reset only by this page's own success (D205).
  const form = useServerForm(
    prefs.data,
    (data) => ({ preferences: data.preferences }),
    { preferences: [] as readonly NotificationPreferenceView[] },
  );
  const draft = form.values.preferences;

  const save = useMutation({
    mutationFn: ({ preferences }: { preferences: typeof draft; mark: number }) =>
      notificationsApi.updatePreferences(preferences),
    onSuccess: async (saved, { mark }) => {
      form.saved(saved, mark);
      queryClient.setQueryData(['notifications', 'preferences'], saved);
      show({ tone: 'success', message: t('notifications.settingsSaved') });
    },
  });

  if (prefs.isLoading) return <LoadingState label={t('common.loading')} />;
  if (prefs.error) return <ErrorState error={prefs.error} onRetry={() => void prefs.refetch()} />;
  const emailAvailable = prefs.data?.emailAvailable ?? false;

  return (
    <>
      <PageHeader
        title={t('notifications.settingsTitle')}
        subtitle={t('notifications.settingsSubtitle')}
        breadcrumbs={[{ label: t('nav.notifications'), to: '/notifications' }, { label: t('notifications.settingsTitle') }]}
      />
      {!emailAvailable ? <Alert tone="info" title={t('notifications.emailNotConfigured')} /> : null}
      <div className="mt-stack">
        <Panel>
          <div className="flex flex-col gap-stack">
            {draft.map((item) => (
              <div key={item.kind} className="flex flex-wrap items-center justify-between gap-stack">
                <span className="min-w-0 text-body text-default-900">{t(`notificationKind.${item.kind}`)}</span>
                <SwitchField
                  label={t('notifications.inApp')}
                  value={item.inAppEnabled}
                  onChange={(value) =>
                    form.set(
                      'preferences',
                      draft.map((entry) => (entry.kind === item.kind ? { ...entry, inAppEnabled: value } : entry)),
                    )
                  }
                />
              </div>
            ))}
            {save.error ? (
              <SaveErrorNotice error={save.error} title={t('notifications.saveFailed')} />
            ) : null}
            <ActionBar>
              <ActionButton
                priority="primary"
                isLoading={save.isPending}
                onPress={() => save.mutate({ preferences: draft, mark: form.mark() })}
              >
                {t('common.save')}
              </ActionButton>
            </ActionBar>
          </div>
        </Panel>
      </div>
    </>
  );
}
